import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import * as hrmsService from "../../services/hrmsService";
import { Field, PrimaryButton, SecondaryButton } from "./hrComponents";
import { formatDays, formatRange, inputStyle, todayYmd } from "./hrUi";

const START_SESSIONS = [
  { label: "Full day", value: "FULL" },
  { label: "First half", value: "FIRST_HALF" },
  { label: "Second half", value: "SECOND_HALF" },
];
const END_SESSIONS = [
  { label: "Full day", value: "FULL" },
  { label: "First half", value: "FIRST_HALF" },
];

// The request as the API wants it, or null while it is not complete enough to check.
export function buildLeavePayload(form) {
  if (!form.leave_type_id || !form.from_date || !form.to_date) return null;
  if (form.to_date < form.from_date) return null;
  const single = form.from_date === form.to_date;
  return {
    leave_type_id: form.leave_type_id,
    from_date: form.from_date,
    to_date: form.to_date,
    from_session: form.from_session || "FULL",
    to_session: single ? "FULL" : form.to_session || "FULL",
    reason: form.reason ? form.reason.trim() : undefined,
    attachment_key: form.attachment_key || undefined,
  };
}

// Apply for leave (or change and resubmit one that was sent back). The rules run on the server:
// every change of dates asks for a preview, so the person sees the days it costs, the balance
// left and any problem before they submit.
export default function LeaveApplyDrawer({ show, onHide, onDone, balances = [], resubmit = null }) {
  const blank = { leave_type_id: null, from_date: todayYmd(), to_date: todayYmd(), from_session: "FULL", to_session: "FULL", reason: "", attachment_key: "", attachment_name: "" };
  const [form, setForm] = useState(blank);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!show) return;
    setPreview(null);
    setForm(
      resubmit
        ? { ...blank, leave_type_id: resubmit.leave_type_id, from_date: resubmit.from_date, to_date: resubmit.to_date, from_session: resubmit.from_session, to_session: resubmit.to_session, reason: resubmit.reason || "", attachment_key: resubmit.attachment_key || "" }
        : blank
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, resubmit]);

  const payload = useMemo(() => buildLeavePayload(form), [form]);

  useEffect(() => {
    if (!show || !payload) {
      setPreview(null);
      return undefined;
    }
    const mine = ++seq.current;
    setChecking(true);
    const timer = setTimeout(async () => {
      const res = await hrmsService.previewLeave(payload).catch((e) => ({ success: false, message: e.message }));
      if (mine !== seq.current) return;
      setChecking(false);
      setPreview(res?.success ? res.data : { can_apply: false, problems: [hrmsService.responseMessage(res)], warnings: [], days: [], balances: [] });
    }, 400);
    return () => clearTimeout(timer);
  }, [show, payload]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const single = form.from_date === form.to_date;
  const typeOptions = balances.map((b) => ({ label: `${b.name} (${formatDays(b.available)} left)`, value: b.leave_type_id }));

  const attach = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await hrmsService.uploadLeaveAttachment(file);
      if (res?.success) set({ attachment_key: res.data.key, attachment_name: res.data.file_name });
      else toast.error(hrmsService.responseMessage(res));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!payload) return toast.error("Choose the leave type and dates");
    setSaving(true);
    try {
      const res = resubmit ? await hrmsService.resubmitLeave(resubmit.id, payload) : await hrmsService.applyLeave(payload);
      if (res?.success) {
        toast.success(res.message || "Leave request sent");
        onDone && onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = !!payload && !!preview && preview.can_apply && !checking && !saving && !uploading;

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={resubmit ? "Change and resubmit leave" : "Apply for leave"}
      width={520}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={submit} disabled={!canSubmit}>
            {resubmit ? "Resubmit" : "Submit"}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Leave type" required>
        <AppDropdown value={form.leave_type_id} options={typeOptions} onChange={(e) => set({ leave_type_id: e.value })} placeholder="Select leave type" />
      </Field>
      <div className="row">
        <div className="col-6">
          <Field label="From" required>
            <input type="date" className="form-control" style={inputStyle} value={form.from_date} onChange={(e) => set({ from_date: e.target.value, to_date: form.to_date < e.target.value ? e.target.value : form.to_date })} />
          </Field>
        </div>
        <div className="col-6">
          <Field label="To" required>
            <input type="date" className="form-control" style={inputStyle} value={form.to_date} min={form.from_date} onChange={(e) => set({ to_date: e.target.value })} />
          </Field>
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <Field label={single ? "Day part" : "Start"}>
            <AppDropdown value={form.from_session} options={START_SESSIONS} onChange={(e) => set({ from_session: e.value })} />
          </Field>
        </div>
        {!single ? (
          <div className="col-6">
            <Field label="End">
              <AppDropdown value={form.to_session} options={END_SESSIONS} onChange={(e) => set({ to_session: e.value })} />
            </Field>
          </div>
        ) : null}
      </div>
      <Field label="Reason">
        <textarea className="form-control" rows={3} maxLength={1000} value={form.reason} onChange={(e) => set({ reason: e.target.value })} />
      </Field>

      {preview?.attachment_required || form.attachment_key ? (
        <Field label="Supporting document" required={!!preview?.attachment_required} hint="PDF, JPG, PNG or DOCX up to 10 MB">
          <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => attach(e.target.files[0])} disabled={uploading} />
          {form.attachment_key ? <div style={{ fontSize: 12, color: "#065F46", marginTop: 4 }}>Attached{form.attachment_name ? `: ${form.attachment_name}` : ""}</div> : null}
        </Field>
      ) : null}

      <div style={{ background: "#F9FAFB", borderRadius: 8, padding: 12, fontSize: 13 }} aria-live="polite">
        {!payload ? (
          <span style={{ color: "#6B7280" }}>Choose the leave type and dates to see what it costs.</span>
        ) : checking || !preview ? (
          <span style={{ color: "#6B7280" }}>Checking…</span>
        ) : (
          <>
            <div style={{ fontWeight: 600 }}>
              {formatRange(form.from_date, form.to_date)} · {formatDays(preview.total_days)}
            </div>
            {(preview.problems || []).map((p) => (
              <div key={p} style={{ color: "#B91C1C", marginTop: 6 }} role="alert">
                {p}
              </div>
            ))}
            {(preview.warnings || []).map((w) => (
              <div key={w} style={{ color: "#92400E", marginTop: 6 }}>
                {w}
              </div>
            ))}
            {(preview.team_on_leave || []).length ? (
              <div style={{ color: "#6B7280", marginTop: 6 }}>Also away: {preview.team_on_leave.map((t) => t.full_name).join(", ")}</div>
            ) : null}
          </>
        )}
      </div>
    </SlidePanel>
  );
}
